import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

/**
 * useSmoothMapTransition Hook
 * 
 * Implements a manual animation loop (game loop style) to handle map movement and rotation
 * during "Driving Mode".
 * 
 * WHY THIS IS NEEDED:
 * Standard CSS transitions and Leaflet animations operate with a static "Pivot Point" (transform-origin)
 * set at the START of the animation. If the map pans (translates) while rotating, the rotation continues
 * to pivot around the *old* center, causing the new center (the car) to swing sideways in a pendulum/arc motion.
 * 
 * To achieve a "Locked Camera" effect where the map rotates exactly around the moving car:
 * 1. We must update the `transform-origin` every single frame to match the car's new pixel position.
 * 2. We must manually compose the CSS transform string (`translate` + `rotate`).
 *    - Critical: `rotate` must be applied AFTER `translate`.
 *    - If `rotate` is applied first (or via the separate `rotate` CSS property), the axes are skewed,
 *      and the subsequent translation moves the map in the wrong direction (e.g., panning "Up" moves "Left").
 */
export const useSmoothMapTransition = ({
    targetCenter,
    targetBearing,
    isDrivingMode,
    isHeadingUp,
    centerOffset
}) => {
    const map = useMap();

    // Store current state in refs to avoid re-renders during animation loop
    const stateRef = useRef({
        lat: targetCenter?.lat || 0,
        lng: targetCenter?.lng || 0,
        bearing: targetBearing || 0,
        offsetX: centerOffset?.x || 0,
        offsetY: centerOffset?.y || 0,
        lastFrameTime: 0
    });

    // Store target state to allow the loop to always access the latest props
    const targetRef = useRef({
        lat: targetCenter?.lat || 0,
        lng: targetCenter?.lng || 0,
        bearing: targetBearing || 0,
        offsetX: centerOffset?.x || 0,
        offsetY: centerOffset?.y || 0
    });

    // Sync ref with props
    useEffect(() => {
        if (!targetCenter) return;
        targetRef.current = {
            lat: targetCenter.lat,
            lng: targetCenter.lng,
            bearing: targetBearing,
            offsetX: centerOffset?.x || 0,
            offsetY: centerOffset?.y || 0
        };
    }, [targetCenter, targetBearing, centerOffset]);

    useEffect(() => {
        if (!isDrivingMode || !isHeadingUp) return;

        let animationFrameId;

        const animate = (time) => {
            const state = stateRef.current;
            const target = targetRef.current;

            // Calculate delta time (capped at 100ms to prevent huge jumps on lag)
            const now = time || performance.now();
            const dt = Math.min((now - state.lastFrameTime) / 1000, 0.1);
            state.lastFrameTime = now;

            // Interpolation factors (adjust for responsiveness vs smoothness)
            const posFactor = 0.1;
            const bearingFactor = 0.05;
            // Use a smaller factor for offset to make HUD transitions feel "deliberate" / slower than driving
            const offsetFactor = 0.05;

            // 1. Interpolate Position (Linear Interpolation)
            const dLat = target.lat - state.lat;
            const dLng = target.lng - state.lng;

            // Snap if distance is too large (teleport)
            if (Math.abs(dLat) > 0.01 || Math.abs(dLng) > 0.01) {
                state.lat = target.lat;
                state.lng = target.lng;
            } else {
                state.lat += dLat * posFactor;
                state.lng += dLng * posFactor;
            }

            // 2. Interpolate Bearing (Shortest path)
            let dBearing = target.bearing - state.bearing;
            if (dBearing > 180) dBearing -= 360;
            if (dBearing < -180) dBearing += 360;

            if (Math.abs(dBearing) > 10) {
                state.bearing = target.bearing;
            } else {
                state.bearing += dBearing * bearingFactor;
            }
            state.bearing = (state.bearing + 360) % 360;

            // 3. Interpolate Offset (Smooth Scroll for HUD)
            const dOffsetX = target.offsetX - state.offsetX;
            const dOffsetY = target.offsetY - state.offsetY;

            if (Math.abs(dOffsetX) > 0.5 || Math.abs(dOffsetY) > 0.5) {
                state.offsetX += dOffsetX * offsetFactor;
                state.offsetY += dOffsetY * offsetFactor;
            } else {
                state.offsetX = target.offsetX;
                state.offsetY = target.offsetY;
            }


            // APPLY TO MAP
            const zoom = map.getZoom();
            // Project Car to pixels (World Space)
            const carPixel = map.project([state.lat, state.lng], zoom);

            // Screen Offset (Pixels) - Use INTERPOLATED state
            const offsetX = state.offsetX;
            const offsetY = state.offsetY;

            const targetPoint = carPixel.subtract([offsetX, offsetY]);
            const targetLatLng = map.unproject(targetPoint, zoom);

            // Update View: Instant (no Leaflet animation)
            // Leaflet sets the 'transform' property to translate the map pane
            map.setView(targetLatLng, zoom, { animate: false });

            // Update Rotation: Manually compose transform
            const mapPane = map.getPanes().mapPane;
            if (mapPane) {
                const carLayerPoint = map.latLngToLayerPoint([state.lat, state.lng]);

                // Set Origin to the car's exact position on the layer
                mapPane.style.transformOrigin = `${carLayerPoint.x}px ${carLayerPoint.y}px`;

                let currentTransform = mapPane.style.transform;

                // Ensure we don't accumulate old rotations
                if (currentTransform.indexOf('rotate(') !== -1) {
                    currentTransform = currentTransform.replace(/rotate\([^)]+\)/g, '');
                }

                // Append Rotation strictly AFTER Translation
                // This ensures the map is translated on standard axes first, then rotated around the car.
                mapPane.style.transform = `${currentTransform} rotate(-${state.bearing}deg)`;

                // Clear any conflicting property
                mapPane.style.rotate = '';
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        // Initialize frame time
        stateRef.current.lastFrameTime = performance.now();
        animationFrameId = requestAnimationFrame(animate);

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            // Cleanup: Reset transform styles when exiting mode
            const mapPane = map.getPanes().mapPane;
            if (mapPane) {
                mapPane.style.transformOrigin = '';
                mapPane.style.transform = mapPane.style.transform.replace(/rotate\([^)]+\)/g, '');
            }
        };
    }, [isDrivingMode, isHeadingUp, map, centerOffset]);
};
